// pages/hauler/JobsScreen.js
// Hauler-facing jobs feed.
//
// Renders two sections:
//   1. Active (claimed / en_route / in_progress) — only when present.
//      Lets the Hauler return to a job in progress before Route tab
//      is wired (Step 6).
//   2. Available — the open pool.
//
// Data comes from useDriverJobs (lifted to HaulerApp; Phase 1.6 hook).
// Cards open JobDetailScreen via onOpenJob.

import React, { useMemo } from "react";
import JobCard from "../../components/hauler/JobCard";
import JobsMap from "../../components/hauler/JobsMap";
import { deriveJobEconomics } from "../../hooks/useJobEconomics";

const COLORS = {
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  green: "var(--hl-green)",
  sky: "var(--hl-sky)",
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function marketLabel(driverProfile) {
  const id = driverProfile?.market_id;
  if (id === "tx-fort-cavazos") return "Fort Cavazos";
  return "Hill AFB";
}

function weeklySummary(completed) {
  if (!completed || !completed.length) return { total: 0, count: 0 };
  const cutoff = Date.now() - WEEK_MS;
  let total = 0;
  let count = 0;
  for (const job of completed) {
    const completedAt = job.completed_at ? new Date(job.completed_at).getTime() : 0;
    if (!completedAt || completedAt < cutoff) continue;
    // Prefer persisted driver_pay_amt over a recomputed split.
    const persisted = Number(job.driver_pay_amt);
    if (Number.isFinite(persisted) && persisted > 0) {
      total += persisted;
      count += 1;
      continue;
    }
    const econ = deriveJobEconomics(job);
    if (econ && econ.driverPay > 0) {
      total += econ.driverPay;
      count += 1;
    }
  }
  return { total: Math.round(total * 100) / 100, count };
}

export default function JobsScreen({
  session,
  driverProfile,
  available = [],
  claimed = [],
  completed = [],
  loading,
  error,
  onOpenJob,
  onClaim,
  viewerIsAdmin = false,
  onRetry,
}) {
  // Surface the technical detail to console for debugging without
  // bleeding it into user-facing UI.
  React.useEffect(() => {
    if (error) console.error("[JobsScreen] feed error:", error);
  }, [error]);
  const summary = useMemo(() => weeklySummary(completed), [completed]);

  return (
    <div style={{ padding: "20px 16px 96px", color: COLORS.text }}>
      {/* Spatial discovery surface: count circles for available jobs only.
          Claimed/active jobs stay in the list below the map. */}
      <JobsMap
        jobs={available}
        height={240}
        onSelectJob={onOpenJob}
        viewerUserId={session?.user?.id || null}
        viewerIsAdmin={viewerIsAdmin}
      />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.muted, marginBottom: 4 }}>
            JOBS
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>Available jobs</div>
        </div>
        <span style={{ fontSize: 12, color: COLORS.sub }}>{marketLabel(driverProfile)}</span>
      </div>

      {/* Weekly earnings card */}
      <div style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: "14px 16px",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: COLORS.sub, textTransform: "uppercase", marginBottom: 4 }}>
            This week
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.green, letterSpacing: -0.5 }}>
            ${summary.total.toFixed(2)}
          </div>
        </div>
        <div style={{ fontSize: 12, color: COLORS.sub, textAlign: "right" }}>
          {summary.count} completed
        </div>
      </div>

      {/* Active section — claimed / en_route / in_progress.
          Uses the same JobCard component as the available pool for visual
          consistency; passes showStatus so the lifecycle state is visible. */}
      {claimed.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <SectionHeader label="Your active jobs" count={claimed.length} />
          {claimed.map(job => (
            <JobCard
              key={job.id}
              job={job}
              onOpen={onOpenJob}
              showStatus
              viewerUserId={session?.user?.id || null}
              viewerIsAdmin={viewerIsAdmin}
            />
          ))}
        </div>
      )}

      {/* Available section */}
      <div>
        {claimed.length > 0 && <SectionHeader label="Available" />}

        {!session && (
          <EmptyState
            title="Sign in required"
            body="Sign in at /haulers to see jobs in your market."
          />
        )}

        {session && loading && (
          <EmptyState title="Loading…" body="Fetching available jobs." />
        )}

        {session && !loading && error && (
          <ErrorState
            title="Couldn't load jobs right now"
            body="Check your connection and try again."
            onRetry={onRetry}
          />
        )}

        {session && !loading && !error && available.length === 0 && (
          <EmptyState
            title="No jobs available right now"
            body="New jobs appear here as they're booked."
          />
        )}

        {session && !loading && !error && available.length > 0 && (
          <div>
            {available.map(job => (
              <JobCard
                key={job.id}
                job={job}
                onOpen={onOpenJob}
                onClaim={onClaim}
                viewerUserId={session?.user?.id || null}
                viewerIsAdmin={viewerIsAdmin}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ label, count }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "4px 2px 10px" }}>
      <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase" }}>
        {label}
      </div>
      {count != null && (
        <div style={{ fontSize: 11, color: COLORS.muted }}>{count}</div>
      )}
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 14,
      padding: "20px 18px",
      textAlign: "center",
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

function ErrorState({ title, body, onRetry }) {
  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 14,
      padding: "20px 18px",
      textAlign: "center",
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.5, marginBottom: 14 }}>{body}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{
            border: `1px solid ${COLORS.sky}`,
            background: "transparent",
            color: COLORS.sky,
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 700,
            padding: "9px 18px",
            borderRadius: 999,
            cursor: "pointer",
            minHeight: 40,
          }}
        >
          Try again
        </button>
      )}
    </div>
  );
}
